import { useState } from "react";

import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import { Avatar, Box, Tooltip } from "@mui/material";

import { BG_ICON_COLOR, EVOTRUX_BORDER_GREY } from "constants/index";
import { getInitialsFromName } from "utils";

interface CompanyLogoTileProps {
  companyName: string;
  logoDownloadUrl: string | null;
  /** Pixel size; always a square. */
  size: number;
  /** Renders the tile as a button with a hover camera overlay. */
  editable?: boolean;
  onEditClick?: () => void;
}

export function CompanyLogoTile(props: CompanyLogoTileProps) {
  const {
    companyName,
    logoDownloadUrl,
    size,
    editable = false,
    onEditClick,
  } = props;
  // The monogram is the tile's background and the image is layered over it, rather
  // than the two being alternatives: handing an <img> a src only starts a download,
  // so branching on "we have a URL" would leave a blank square until the file landed.
  // Presigned URLs also expire, so a load that never resolves is a normal state and
  // simply never covers the initials. Storing the URL that failed rather than a flag
  // lets a new URL retry on its own, with no effect needed to clear it.
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null);
  // The logo's backing — its white fill, and the tile's ring — waits for the file,
  // because both paint as soon as the elements are laid out and would otherwise
  // cover the initials with an empty square for the whole download.
  const [loadedLogoUrl, setLoadedLogoUrl] = useState<string | null>(null);
  // The null check is load-bearing: without it a company with no logo matches
  // null against null and draws the logo's ring around bare initials.
  const isLogoLoaded =
    logoDownloadUrl !== null && loadedLogoUrl === logoDownloadUrl;

  const tile = (
    <Avatar
      variant="rounded"
      sx={{
        width: size,
        height: size,
        borderRadius: 2,
        backgroundColor: BG_ICON_COLOR,
        fontSize: size / 3,
        // The ring belongs on the clipper, not the clipped. Avatar sets
        // overflow:hidden, so a border on the image inside — same radius, same
        // box — is shaved away around each corner and reads as four cut lines.
        border: isLogoLoaded ? `1px solid ${EVOTRUX_BORDER_GREY}` : "none",
        // No box-sizing reset in this app, so the border would otherwise grow
        // the tile by 2px and shift the row it sits in.
        boxSizing: "border-box",
      }}
    >
      {getInitialsFromName(companyName)}
      {logoDownloadUrl !== null && failedLogoUrl !== logoDownloadUrl && (
        <Box
          component="img"
          src={logoDownloadUrl}
          alt={`${companyName} logo`}
          // A cached file can finish loading before React attaches onLoad, which
          // would leave the backing off for good and show the initials through a
          // logo with a transparent background. A cached *failure* is complete
          // too, hence naturalWidth: onError unmounts the image but leaves this
          // state set, which would ring the initials that come back.
          ref={(node: HTMLImageElement | null) => {
            if (node?.complete && node.naturalWidth > 0) {
              setLoadedLogoUrl(logoDownloadUrl);
            }
          }}
          onLoad={() => setLoadedLogoUrl(logoDownloadUrl)}
          onError={() => setFailedLogoUrl(logoDownloadUrl)}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            // Contained rather than cover: Avatar's own object-fit crops, and
            // wordmarks have to letterbox instead.
            objectFit: "contain",
            borderRadius: 2,
            backgroundColor: isLogoLoaded ? "common.white" : "transparent",
          }}
        />
      )}
    </Avatar>
  );

  if (!editable) {
    return tile;
  }

  // A real <button> rather than a hover-only affordance, so the modal is
  // reachable by keyboard and announced to assistive technology. The camera
  // overlay is an additional visual cue layered on top.
  return (
    <Tooltip title="Edit company logo">
      <Box
        component="button"
        type="button"
        aria-label="Edit company logo"
        onClick={onEditClick}
        sx={{
          position: "relative",
          width: size,
          height: size,
          padding: 0,
          border: "none",
          background: "none",
          cursor: "pointer",
          borderRadius: 2,
          "&:hover .logo-edit-overlay, &:focus-visible .logo-edit-overlay": {
            opacity: 1,
          },
        }}
      >
        {tile}
        <Box
          className="logo-edit-overlay"
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 2,
            backgroundColor: "rgba(0, 0, 0, 0.45)",
            color: "common.white",
            opacity: 0,
            transition: "opacity 150ms ease-in-out",
          }}
        >
          <PhotoCameraOutlinedIcon fontSize="small" />
        </Box>
      </Box>
    </Tooltip>
  );
}
